import { useMemo } from 'react';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { LinePath, Line, Area } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { curveMonotoneX } from '@visx/curve';
import type { TaperDayWire } from '@shared/types';
import { pickWeekTickValues } from '../../lib/chartTicks';

export interface OverlayPoint { i: number; mg: number; }

interface TaperChartProps {
  days: TaperDayWire[];
  todayIndex?: number;
  actualByDate?: Record<string, number>;
  // Inclusive index range to display. When omitted, the chart shows
  // the entire plan (existing behavior). The TaperScreen uses these
  // to drive Week / Month / Full views from a SegmentedControl.
  windowStartIndex?: number;
  windowEndIndex?: number;
  // Optional "previous period" actual line. TaperScreen computes this
  // by shifting actualByDate back by the window length so users can
  // compare "this period vs last period" without leaving the screen.
  // Rendered as a thin low-opacity ghost line behind the main actual.
  overlayPoints?: OverlayPoint[];
  // Optional trailing 7-day average band. TaperScreen only populates
  // this in Month/Full views (where same-day comparisons get too
  // noisy). Rendered as a soft area band behind the actual line so
  // the trend reads even when day-to-day actuals jump around.
  rolling7AvgPoints?: OverlayPoint[];
}

interface Point { i: number; mg: number; isHold: boolean; }

export function TaperChart(props: TaperChartProps) {
  return (
    <div style={{ width: '100%', height: '50vh', maxHeight: 360 }}>
      <ParentSize>
        {({ width, height }) => <TaperChartInner {...props} width={width} height={height} />}
      </ParentSize>
    </div>
  );
}

// Compact, integer-day label for narrow windows (e.g. "Mon 5/14").
// We use the locale's short weekday but build the numeric date by hand
// so the format is stable across locales — date pickers vary wildly
// otherwise.
function shortDayLabel(dayIndex: number, days: TaperDayWire[]): string {
  const day = days[Math.max(0, Math.min(days.length - 1, Math.round(dayIndex)))];
  if (!day) return '';
  const d = new Date(`${day.date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const wd = d.toLocaleDateString(undefined, { weekday: 'short' });
  return `${wd} ${d.getMonth() + 1}/${d.getDate()}`;
}

function TaperChartInner({
  days,
  todayIndex,
  actualByDate,
  windowStartIndex,
  windowEndIndex,
  overlayPoints,
  rolling7AvgPoints,
  width,
  height,
}: TaperChartProps & { width: number; height: number }) {
  // Right margin sized to fit the half-width of the last x-axis label
  // (date labels like "Wed 5/20" can be ~52 px wide; anchor=middle
  // means half hangs past the tick).
  const margin = { top: 16, right: 28, bottom: 28, left: 36 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const lastIndex = Math.max(0, days.length - 1);
  const startIdx = Math.max(0, Math.min(lastIndex, windowStartIndex ?? 0));
  const endIdx = Math.max(startIdx, Math.min(lastIndex, windowEndIndex ?? lastIndex));
  const windowLen = endIdx - startIdx + 1;

  const targetPoints: Point[] = useMemo(
    () => days
      .filter((d) => d.dayIndex >= startIdx && d.dayIndex <= endIdx)
      .map((d) => ({ i: d.dayIndex, mg: d.targetMg, isHold: d.isHold })),
    [days, startIdx, endIdx],
  );
  const actualPoints: Point[] = useMemo(
    () => {
      if (!actualByDate) return [];
      return days
        .filter((d) => d.dayIndex >= startIdx && d.dayIndex <= endIdx && actualByDate[d.date] !== undefined)
        .map((d) => ({ i: d.dayIndex, mg: actualByDate[d.date], isHold: d.isHold }));
    },
    [days, actualByDate, startIdx, endIdx],
  );

  // Overlay points already pre-filtered & re-indexed by the caller to
  // match the visible window's x positions. We just clip defensively
  // here in case a stale window passes through during a re-render.
  const overlayInWindow: OverlayPoint[] = useMemo(
    () => (overlayPoints ?? []).filter((p) => p.i >= startIdx && p.i <= endIdx),
    [overlayPoints, startIdx, endIdx],
  );
  // Same defensive clip for the rolling-average points.
  const rollingInWindow: OverlayPoint[] = useMemo(
    () => (rolling7AvgPoints ?? []).filter((p) => p.i >= startIdx && p.i <= endIdx),
    [rolling7AvgPoints, startIdx, endIdx],
  );

  // Y-axis auto-scales to the visible window so day-to-day movement is
  // readable in Week view (otherwise the whole 0..baseline range stays
  // fixed and a 2 mg drop looks tiny). Anchored at 0 to keep "0 = done"
  // visible. Adherence band (target × 1.1) and overlay points are
  // included so they never clip off the top.
  const maxMg = Math.max(
    1,
    ...targetPoints.map((p) => p.mg * 1.1),
    ...actualPoints.map((p) => p.mg),
    ...overlayInWindow.map((p) => p.mg),
    ...rollingInWindow.map((p) => p.mg),
  );

  // For very short windows (≤ 8 days) we want per-day date labels.
  // Wider windows use the `wk N` formatter — but we pin ticks to exact
  // week-boundary day-indices instead of letting visx pick floats.
  // Without that, two adjacent visx-chosen ticks can both round to the
  // same `wk N` and the axis shows duplicate labels (visible in the
  // Month view). Decimation via `pickWeekTickValues` keeps labels
  // legible on narrow widths — at 250 px inner width, a 12-week Full
  // view drops from 12 cramped ticks to 4 spaced ones.
  const useDateLabels = windowLen <= 8;
  const weekTickValues = useMemo<number[]>(
    () =>
      useDateLabels
        ? []
        : pickWeekTickValues({ startIndex: startIdx, endIndex: endIdx, innerWidth: innerW }),
    [useDateLabels, startIdx, endIdx, innerW],
  );
  const numTicks = useDateLabels ? Math.min(4, windowLen) : undefined;

  const x = scaleLinear<number>({
    domain: [startIdx, startIdx === endIdx ? startIdx + 1 : endIdx],
    range: [0, innerW],
  });
  const y = scaleLinear<number>({ domain: [0, maxMg * 1.1], range: [innerH, 0], nice: true });

  if (width === 0) return null;
  const todayInWindow =
    typeof todayIndex === 'number' && todayIndex >= startIdx && todayIndex <= endIdx;

  return (
    <svg width={width} height={height} role="img" aria-label="taper schedule chart">
      <Group left={margin.left} top={margin.top}>
        <GridRows scale={y} width={innerW} stroke="var(--chart-grid)" strokeOpacity={0.6} />

        {/* Adherence band: target ± 10 %. Sits behind the dashed line
            so users see "you're in the zone" without obscuring data. */}
        {targetPoints.length > 1 ? (
          <Area<Point>
            data={targetPoints}
            x={(d) => x(d.i)}
            y0={(d) => y(d.mg * 0.9)}
            y1={(d) => y(d.mg * 1.1)}
            curve={curveMonotoneX}
            fill="var(--chart-target)"
            fillOpacity={0.08}
          />
        ) : null}

        {/* Trailing 7-day average band — Month/Full views only. Drawn
            BEFORE the previous-period overlay so it reads as smoothed
            context (a soft trend), not as primary data. */}
        {rollingInWindow.length > 1 ? (
          <Area<OverlayPoint>
            data={rollingInWindow}
            x={(d) => x(d.i)}
            y0={() => y(0)}
            y1={(d) => y(d.mg)}
            curve={curveMonotoneX}
            fill="var(--chart-actual)"
            fillOpacity={0.1}
          />
        ) : null}
        {rollingInWindow.length > 1 ? (
          <LinePath<OverlayPoint>
            data={rollingInWindow}
            x={(d) => x(d.i)}
            y={(d) => y(d.mg)}
            curve={curveMonotoneX}
            stroke="var(--chart-actual)"
            strokeWidth={1.25}
            strokeOpacity={0.45}
            strokeLinecap="round"
          />
        ) : null}

        {/* Previous-period overlay (Compare-to-previous toggle). Drawn
            before the main actual so it reads as a ghost, not noise. */}
        {overlayInWindow.length > 1 ? (
          <LinePath<OverlayPoint>
            data={overlayInWindow}
            x={(d) => x(d.i)}
            y={(d) => y(d.mg)}
            curve={curveMonotoneX}
            stroke="var(--chart-actual)"
            strokeWidth={1.5}
            strokeOpacity={0.4}
            strokeLinecap="round"
            strokeDasharray="2,3"
          />
        ) : null}
        {overlayInWindow.map((p) => (
          <circle
            key={`overlay-${p.i}`}
            cx={x(p.i)}
            cy={y(p.mg)}
            r={2}
            fill="var(--chart-actual)"
            fillOpacity={0.4}
          />
        ))}

        <LinePath<Point>
          data={targetPoints}
          x={(d) => x(d.i)}
          y={(d) => y(d.mg)}
          curve={curveMonotoneX}
          stroke="var(--chart-target)"
          strokeWidth={2}
          strokeDasharray="6,4"
          strokeLinecap="round"
        />

        {actualPoints.length > 1 ? (
          <LinePath<Point>
            data={actualPoints}
            x={(d) => x(d.i)}
            y={(d) => y(d.mg)}
            curve={curveMonotoneX}
            stroke="var(--chart-actual)"
            strokeWidth={2.5}
            strokeLinecap="round"
          />
        ) : null}
        {actualPoints.map((p) => (
          <circle
            key={p.i}
            cx={x(p.i)}
            cy={y(p.mg)}
            r={3.5}
            fill="var(--chart-actual)"
          />
        ))}

        {/* Tomorrow's target — a small outline circle at todayIndex+1
            on the target line. One glance at "what's next." */}
        {typeof todayIndex === 'number'
          && todayIndex + 1 >= startIdx
          && todayIndex + 1 <= endIdx
          && days[todayIndex + 1] ? (
          <circle
            cx={x(todayIndex + 1)}
            cy={y(days[todayIndex + 1].targetMg)}
            r={4}
            fill="var(--color-bg-canvas)"
            stroke="var(--chart-today-marker)"
            strokeWidth={1.5}
          />
        ) : null}

        {todayInWindow ? (
          <Line
            from={{ x: x(todayIndex as number), y: 0 }}
            to={{ x: x(todayIndex as number), y: innerH }}
            stroke="var(--chart-today-marker)"
            strokeWidth={1.5}
          />
        ) : null}

        <AxisBottom
          top={innerH}
          scale={x}
          stroke="var(--chart-axis)"
          tickStroke="var(--chart-axis)"
          tickLabelProps={() => ({
            fill: 'var(--chart-axis)',
            fontSize: 11,
            textAnchor: 'middle',
            dy: '0.25em',
          })}
          numTicks={numTicks}
          tickValues={useDateLabels ? undefined : weekTickValues}
          tickFormat={(v) =>
            useDateLabels
              ? shortDayLabel(v as number, days)
              : `wk ${Math.round((v as number) / 7)}`
          }
        />
        <AxisLeft
          scale={y}
          stroke="var(--chart-axis)"
          tickStroke="var(--chart-axis)"
          tickLabelProps={() => ({
            fill: 'var(--chart-axis)',
            fontSize: 11,
            textAnchor: 'end',
            dx: '-0.25em',
            dy: '0.25em',
          })}
          numTicks={5}
          tickFormat={(v) => `${v}`}
        />
      </Group>
    </svg>
  );
}
